import { useEffect, useMemo, useState } from "react";
import client from "../api/client";

const companyId = () => Number(localStorage.getItem("companyId") || 0);
const today = () => new Date().toISOString().split("T")[0];

const val = (r, ...keys) => {
  for (const k of keys) {
    const x = r?.[k];
    if (x !== undefined && x !== null) return x;
  }
  return "";
};

const safe = (v) => {
  if (v === null || v === undefined) return "";
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  return "";
};

const arr = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.result)) return data.result;
  return [];
};

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const money = (v) =>
  num(v).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const errText = (e, fallback) => {
  const d = e?.response?.data;
  if (typeof d === "string" && d.trim()) return d;
  if (typeof d?.message === "string" && d.message.trim()) return d.message;
  if (typeof d?.title === "string" && d.title.trim()) return d.title;
  if (typeof e?.message === "string" && e.message.trim()) return e.message;
  return fallback;
};

const styles = {
  input: { height: 30, fontSize: 11, borderRadius: 7 },
  btn: { height: 30, fontSize: 11, borderRadius: 7, fontWeight: 700 },
  td: { fontSize: 11, padding: "6px 8px", whiteSpace: "nowrap" },
};

export default function SalesmanCommissionPage() {
  const [rows, setRows] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    salesmanCode: "",
    salesmanName: "",
    mobileNo: "",
    emailId: "",
    areaName: "",
    commissionPercent: 5,
    targetAmount: 0,
    joiningDate: today(),
    isActive: true,
    notes: "",
  });

  const stats = useMemo(
    () => ({
      salesmen: rows.length,
      sales: rows.reduce(
        (s, x) => s + num(val(x, "TotalSales", "totalSales")),
        0,
      ),
      commission: rows.reduce(
        (s, x) => s + num(val(x, "TotalCommission", "totalCommission")),
        0,
      ),
      pending: rows.reduce(
        (s, x) => s + num(val(x, "PendingCommission", "pendingCommission")),
        0,
      ),
    }),
    [rows],
  );

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    await Promise.allSettled([loadRows(), loadCommissions(), loadNextCode()]);
    setLoading(false);
  };

  const loadNextCode = async () => {
    try {
      const res = await client.get("/salesmen/next-code", {
        params: { companyId: companyId() || undefined },
      });

      const code = safe(
        val(res.data, "SalesmanCode", "salesmanCode", "Code", "code"),
      );

      setForm((x) => ({
        ...x,
        salesmanCode: code || `SM-${Date.now().toString().slice(-5)}`,
      }));
    } catch {
      setForm((x) => ({
        ...x,
        salesmanCode: `SM-${Date.now().toString().slice(-5)}`,
      }));
    }
  };

  const loadRows = async () => {
    try {
      const res = await client.get("/salesmen", {
        params: { companyId: companyId() || undefined },
      });

      setRows(arr(res.data));
    } catch (e) {
      console.error(e);
      setRows([]);
    }
  };

  const loadCommissions = async () => {
    try {
      const res = await client.get("/salesmen/commissions", {
        params: { companyId: companyId() || undefined },
      });

      setCommissions(arr(res.data));
    } catch (e) {
      console.error(e);
      setCommissions([]);
    }
  };

  const save = async () => {
    if (!safe(form.salesmanCode).trim()) return alert("Salesman code required");
    if (!safe(form.salesmanName).trim()) return alert("Salesman name required");
    if (num(form.commissionPercent) < 0 || num(form.commissionPercent) > 100)
      return alert("Commission % must be between 0 and 100");
    if (num(form.targetAmount) < 0)
      return alert("Target amount cannot be negative");

    try {
      setSaving(true);

      await client.post("/salesmen", {
        companyId: companyId(),
        salesmanCode: safe(form.salesmanCode).trim(),
        salesmanName: safe(form.salesmanName).trim(),
        mobileNo: safe(form.mobileNo).trim(),
        emailId: safe(form.emailId).trim(),
        areaName: safe(form.areaName).trim(),
        commissionPercent: num(form.commissionPercent),
        targetAmount: num(form.targetAmount),
        joiningDate: form.joiningDate || today(),
        isActive: !!form.isActive,
        notes: safe(form.notes).trim(),
      });

      alert("Salesman saved successfully");

      setForm({
        salesmanCode: "",
        salesmanName: "",
        mobileNo: "",
        emailId: "",
        areaName: "",
        commissionPercent: 5,
        targetAmount: 0,
        joiningDate: today(),
        isActive: true,
        notes: "",
      });

      await loadNextCode();
      await loadRows();
      await loadCommissions();
    } catch (e) {
      console.error(e);
      alert(errText(e, "Failed to save salesman"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container-fluid py-2" style={{ fontSize: 11, padding: 0 }}>
      <div
        style={{
          background: "#fff",
          borderRadius: 14,
          overflow: "hidden",
          boxShadow: "0 2px 10px rgba(0,0,0,.08)",
        }}
      >
        <div
          style={{
            background:
              "linear-gradient(90deg, rgb(11, 79, 74), rgb(15, 118, 110))",
            color: "#fff",
            padding: "9px 12px",
          }}
        >
          <h6 style={{ fontSize: 13, fontWeight: 800, margin: 0 }}>
            <i className="bi bi-person-badge me-2"></i>
            Salesman & Commission Management
          </h6>

          <div style={{ fontSize: 10, opacity: 0.85 }}>
            Territory-wise sales tracking with commission analysis
          </div>
        </div>

        <div style={{ padding: 10 }}>
          <div className="row g-2 mb-2">
            <Stat title="Salesmen" value={stats.salesmen} />
            <Stat title="Sales" value={`₹ ${money(stats.sales)}`} />
            <Stat title="Commission" value={`₹ ${money(stats.commission)}`} />
            <Stat title="Pending" value={`₹ ${money(stats.pending)}`} />
          </div>

          <Section title="Salesman Master" />

          <div className="row g-2 mb-2">
            <Field label="Code">
              <input
                className="form-control form-control-sm"
                style={styles.input}
                value={form.salesmanCode}
                onChange={(e) =>
                  setForm({ ...form, salesmanCode: e.target.value })
                }
              />
            </Field>

            <Field label="Name" col="col-md-3">
              <input
                className="form-control form-control-sm"
                style={styles.input}
                value={form.salesmanName}
                onChange={(e) =>
                  setForm({ ...form, salesmanName: e.target.value })
                }
              />
            </Field>

            <Field label="Mobile">
              <input
                className="form-control form-control-sm"
                style={styles.input}
                value={form.mobileNo}
                onChange={(e) => setForm({ ...form, mobileNo: e.target.value })}
              />
            </Field>

            <Field label="Email" col="col-md-3">
              <input
                type="email"
                className="form-control form-control-sm"
                style={styles.input}
                value={form.emailId}
                onChange={(e) => setForm({ ...form, emailId: e.target.value })}
              />
            </Field>

            <Field label="Area">
              <input
                className="form-control form-control-sm"
                style={styles.input}
                value={form.areaName}
                onChange={(e) => setForm({ ...form, areaName: e.target.value })}
              />
            </Field>

            <Field label="Commission %">
              <input
                type="number"
                min="0"
                max="100"
                step="0.01"
                className="form-control form-control-sm"
                style={styles.input}
                value={form.commissionPercent}
                onChange={(e) =>
                  setForm({ ...form, commissionPercent: e.target.value })
                }
              />
            </Field>

            <Field label="Target">
              <input
                type="number"
                min="0"
                step="0.01"
                className="form-control form-control-sm"
                style={styles.input}
                value={form.targetAmount}
                onChange={(e) =>
                  setForm({ ...form, targetAmount: e.target.value })
                }
              />
            </Field>

            <Field label="Joining Date">
              <input
                type="date"
                className="form-control form-control-sm"
                style={styles.input}
                value={form.joiningDate}
                onChange={(e) =>
                  setForm({ ...form, joiningDate: e.target.value })
                }
              />
            </Field>

            <Field label="Status">
              <select
                className="form-select form-select-sm"
                style={styles.input}
                value={form.isActive ? "ACTIVE" : "INACTIVE"}
                onChange={(e) =>
                  setForm({ ...form, isActive: e.target.value === "ACTIVE" })
                }
              >
                <option value="ACTIVE">ACTIVE</option>
                <option value="INACTIVE">INACTIVE</option>
              </select>
            </Field>

            <Field label="Notes" col="col-md-3">
              <input
                className="form-control form-control-sm"
                style={styles.input}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </Field>
          </div>

          <div className="d-flex justify-content-end gap-2 mb-3">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              style={styles.btn}
              onClick={() => window.print()}
            >
              Print
            </button>

            <button
              type="button"
              className="btn btn-success btn-sm"
              style={styles.btn}
              disabled={saving || loading}
              onClick={save}
            >
              {saving ? "Saving..." : "Save Salesman"}
            </button>
          </div>

          <Section title="Salesman Register" />

          <TableWrap>
            <table
              className="table table-bordered table-hover align-middle"
              style={{ fontSize: 11, minWidth: 1300, marginBottom: 0 }}
            >
              <thead>
                <tr>
                  {[
                    "Code",
                    "Name",
                    "Area",
                    "Commission %",
                    "Target",
                    "Sales",
                    "Commission",
                    "Paid",
                    "Pending",
                    "Status",
                  ].map((h) => (
                    <TH key={h}>{h}</TH>
                  ))}
                </tr>
              </thead>

              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan="10"
                      className="text-center text-muted"
                      style={{ padding: 18 }}
                    >
                      {loading ? "Loading..." : "No salesmen found"}
                    </td>
                  </tr>
                ) : (
                  rows.map((r, index) => (
                    <tr key={val(r, "Id", "id") || index}>
                      <td style={styles.td}>
                        {safe(val(r, "SalesmanCode", "salesmanCode"))}
                      </td>

                      <td style={{ ...styles.td, fontWeight: 700 }}>
                        {safe(val(r, "SalesmanName", "salesmanName"))}
                      </td>

                      <td style={styles.td}>
                        {safe(val(r, "AreaName", "areaName"))}
                      </td>

                      <td style={styles.td}>
                        {money(
                          val(r, "CommissionPercent", "commissionPercent"),
                        )}
                      </td>

                      <td style={styles.td}>
                        ₹ {money(val(r, "TargetAmount", "targetAmount"))}
                      </td>

                      <td style={styles.td}>
                        ₹ {money(val(r, "TotalSales", "totalSales"))}
                      </td>

                      <td style={styles.td}>
                        ₹ {money(val(r, "TotalCommission", "totalCommission"))}
                      </td>

                      <td style={styles.td}>
                        ₹ {money(val(r, "PaidCommission", "paidCommission"))}
                      </td>

                      <td
                        style={{
                          ...styles.td,
                          color: "#dc2626",
                          fontWeight: 700,
                        }}
                      >
                        ₹{" "}
                        {money(
                          val(r, "PendingCommission", "pendingCommission"),
                        )}
                      </td>

                      <td style={styles.td}>
                        {val(r, "IsActive", "isActive") ? "ACTIVE" : "INACTIVE"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </TableWrap>

          <Section title="Commission Register" />

          <TableWrap>
            <table
              className="table table-bordered table-hover align-middle"
              style={{ fontSize: 11, minWidth: 1400, marginBottom: 0 }}
            >
              <thead>
                <tr>
                  {[
                    "Invoice",
                    "Date",
                    "Salesman",
                    "Customer",
                    "Sales",
                    "Commission %",
                    "Commission",
                    "Paid",
                    "Balance",
                    "Status",
                  ].map((h) => (
                    <TH key={h}>{h}</TH>
                  ))}
                </tr>
              </thead>

              <tbody>
                {commissions.length === 0 ? (
                  <tr>
                    <td
                      colSpan="10"
                      className="text-center text-muted"
                      style={{ padding: 18 }}
                    >
                      {loading ? "Loading..." : "No commissions found"}
                    </td>
                  </tr>
                ) : (
                  commissions.map((r, index) => (
                    <tr key={val(r, "Id", "id") || index}>
                      <td style={styles.td}>
                        {safe(val(r, "InvoiceNo", "invoiceNo"))}
                      </td>

                      <td style={styles.td}>
                        {safe(val(r, "InvoiceDate", "invoiceDate")).slice(
                          0,
                          10,
                        )}
                      </td>

                      <td style={{ ...styles.td, fontWeight: 700 }}>
                        {safe(val(r, "SalesmanName", "salesmanName"))}
                      </td>

                      <td style={styles.td}>
                        {safe(val(r, "CustomerName", "customerName"))}
                      </td>

                      <td style={styles.td}>
                        ₹ {money(val(r, "SalesAmount", "salesAmount"))}
                      </td>

                      <td style={styles.td}>
                        {money(
                          val(r, "CommissionPercent", "commissionPercent"),
                        )}
                      </td>

                      <td style={styles.td}>
                        ₹{" "}
                        {money(val(r, "CommissionAmount", "commissionAmount"))}
                      </td>

                      <td style={styles.td}>
                        ₹ {money(val(r, "PaidAmount", "paidAmount"))}
                      </td>

                      <td
                        style={{
                          ...styles.td,
                          color: "#dc2626",
                          fontWeight: 700,
                        }}
                      >
                        ₹ {money(val(r, "BalanceAmount", "balanceAmount"))}
                      </td>

                      <td style={styles.td}>
                        {safe(val(r, "Status", "status")) || "-"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </TableWrap>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, col = "col-md-2" }) {
  return (
    <div className={col}>
      <label
        style={{
          fontSize: 10,
          fontWeight: 700,
          marginBottom: 3,
          color: "#495057",
        }}
      >
        {label}
      </label>
      {children}
    </div>
  );
}

function Stat({ title, value }) {
  return (
    <div className="col-md-3">
      <div
        style={{
          border: "1px solid #e9ecef",
          background: "#f8fafc",
          borderRadius: 10,
          padding: 9,
          borderTop: "3px solid #0f766e",
        }}
      >
        <div style={{ fontSize: 10, color: "#64748b", fontWeight: 800 }}>
          {title}
        </div>

        <div style={{ fontSize: 15, color: "#0f766e", fontWeight: 900 }}>
          {safe(value)}
        </div>
      </div>
    </div>
  );
}

function Section({ title }) {
  return (
    <div
      style={{
        fontSize: 11,
        fontWeight: 800,
        color: "#0f766e",
        borderBottom: "1px solid #e9ecef",
        paddingBottom: 4,
        marginBottom: 8,
      }}
    >
      {title}
    </div>
  );
}

function TableWrap({ children }) {
  return (
    <div
      style={{
        maxHeight: 350,
        overflow: "auto",
        border: "1px solid #e9ecef",
        borderRadius: 10,
      }}
      className="mb-3"
    >
      {children}
    </div>
  );
}

function TH({ children }) {
  return (
    <th
      style={{
        fontSize: 10,
        color: "#fff",
        background: "#07111f",
        padding: "7px 8px",
        whiteSpace: "nowrap",
        position: "sticky",
        top: 0,
      }}
    >
      {children}
    </th>
  );
}
